import React, { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import BrandSelect from 'features/playground/components/BrandSelect';
import { getLineage } from 'features/evals3/api/evals3Api';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';
import { boolWords } from 'features/evals3/components/StepAnswer';

/**
 * Eval conditions on a fill: which rows qualify by what the evals said.
 * Each line is one eval and one test; lines AND together.
 *
 *   verdict failed / passed, did not fail ("passed every eval" is passed + did not fail)
 *   marked wrong / right, not marked wrong
 *   reason code is …
 *   score < x
 *   step S answered …   (typed by the step: yes/no, one of its options, a number)
 *
 * The step list comes from the eval's current graph. Emits the server grammar:
 * [{ lineage_id, verdict?, marked?, reason_codes?, score?: {op, value}, step?: {node_id, field, op, value}, negate? }]
 */

export const WHAT = [
  { value: 'fail', label: 'failed' },
  { value: 'pass', label: 'passed' },
  { value: 'not_fail', label: 'did not fail' },
  { value: 'not_wrong', label: 'not marked wrong' },
  { value: 'wrong', label: 'marked wrong' },
  { value: 'right', label: 'marked right' },
  { value: 'reason', label: 'reason code' },
  { value: 'score', label: 'score' },
  { value: 'step', label: 'a step answered' },
];
const NUM_OPS = [['lt', '<'], ['lte', '≤'], ['gte', '≥'], ['gt', '>'], ['eq', '=']];

export function newCondition() {
  return { key: `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, lineage_id: '', what: 'fail', reason: '', op: 'lt', value: '', node_id: '', field: '', stepValue: null };
}

/** The UI lines → the filter's eval_conditions (incomplete lines are left out). */
export function conditionsToFilter(lines) {
  const out = [];
  for (const c of lines || []) {
    const base = c.lineage_id ? { lineage_id: c.lineage_id } : {};
    if (c.what === 'fail' || c.what === 'pass') out.push({ ...base, verdict: c.what });
    else if (c.what === 'not_fail') out.push({ ...base, verdict: 'fail', negate: true });
    else if (c.what === 'not_wrong') out.push({ ...base, marked: 'wrong', negate: true });
    else if (c.what === 'wrong' || c.what === 'right') out.push({ ...base, marked: c.what });
    else if (c.what === 'reason') { const codes = String(c.reason || '').split(',').map((s) => s.trim()).filter(Boolean); if (codes.length) out.push({ ...base, reason_codes: codes }); }
    else if (c.what === 'score') { const v = Number(c.value); if (c.value !== '' && Number.isFinite(v)) out.push({ ...base, score: { op: c.op || 'lt', value: v } }); }
    else if (c.what === 'step') {
      if (!c.lineage_id || !c.node_id || !c.field || c.stepValue === null || c.stepValue === undefined || c.stepValue === '') continue;
      const step = { node_id: c.node_id, field: c.field };
      if (typeof c.stepValue === 'boolean') Object.assign(step, { op: 'eq', value: c.stepValue });
      else if (Array.isArray(c.stepValue)) { if (!c.stepValue.length) continue; Object.assign(step, { op: 'in', value: c.stepValue }); }
      else if (typeof c.stepValue === 'number') Object.assign(step, { op: c.op || 'lt', value: c.stepValue });
      else Object.assign(step, { op: 'eq', value: String(c.stepValue) });
      out.push({ ...base, step });
    }
  }
  return out;
}

/** The reverse: stored conditions back into editable lines (Update with the same filters, then changed). */
export function filterToConditions(conds) {
  let n = 0;
  const key = () => `c${Date.now().toString(36)}${n++}`;
  const out = [];
  for (const c of Array.isArray(conds) ? conds : []) {
    if (!c || typeof c !== 'object') continue;
    const base = { key: key(), lineage_id: c.lineage_id || '' };
    if (c.negate && c.verdict === 'fail') out.push({ ...base, what: 'not_fail' });
    else if (c.negate && c.marked === 'wrong') out.push({ ...base, what: 'not_wrong' });
    else if (c.verdict) out.push({ ...base, what: c.verdict });
    else if (c.marked) out.push({ ...base, what: c.marked });
    else if (Array.isArray(c.reason_codes)) out.push({ ...base, what: 'reason', reason: c.reason_codes.join(', ') });
    else if (c.score) out.push({ ...base, what: 'score', op: c.score.op, value: String(c.score.value) });
    else if (c.step) out.push({ ...base, what: 'step', node_id: c.step.node_id, field: c.step.field, op: c.step.op, stepValue: c.step.value });
  }
  return out;
}

/** One condition in words, for summaries. */
export function conditionText(c, names = {}) {
  const who = c.lineage_id ? (names[c.lineage_id] || 'eval') : 'any eval';
  if (c.negate && c.verdict === 'fail') return `${who} did not fail`;
  if (c.negate && c.marked === 'wrong') return `${who} not marked wrong`;
  if (c.verdict) return `${who} ${c.verdict === 'fail' ? 'failed' : 'passed'}`;
  if (c.marked) return `${who} marked ${c.marked}`;
  if (c.reason_codes) return `${who} reason ${c.reason_codes.join('/')}`;
  if (c.score) return `${who} score ${(NUM_OPS.find(([k]) => k === c.score.op) || ['', c.score.op])[1]} ${c.score.value}`;
  if (c.step) {
    const v = Array.isArray(c.step.value) ? c.step.value.join('/') : typeof c.step.value === 'number' ? `${(NUM_OPS.find(([k]) => k === c.step.op) || ['', '='])[1]} ${c.step.value}` : String(c.step.value);
    return `${who} · ${c.step.node_id} ${c.step.field} ${v}`;
  }
  return who;
}

export const stepsOf = (graph) => Object.entries((graph && graph.nodes) || {})
  .filter(([, n]) => n && n.type !== 'verdict' && n.output && Object.keys(n.output).length)
  .map(([id, n]) => ({ id, name: n.name || id, output: n.output }));

export function StepValue({ field, value, op, onValue, onOp }) {
  const btn = 'rounded border px-2 py-0.5 text-[11px]';
  const on = 'border-[color:var(--border-strong)] bg-surface-hover font-semibold text-[color:var(--text-primary)]';
  const off = 'border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-surface-hover';
  if (!field) return null;
  if (field.type === 'boolean') {
    const [yes, no] = boolWords(field.name);
    return (
      <div className="flex gap-1">
        {[[yes, true], [no, false]].map(([l, v]) => (
          <button key={l} type="button" aria-pressed={value === v} onClick={() => onValue(v)} className={`${btn} ${value === v ? on : off}`}>{l}</button>
        ))}
      </div>
    );
  }
  if (field.type === 'enum') {
    const picked = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-1">
        {(field.values || []).map((opt) => {
          const isOn = picked.includes(opt);
          return <button key={opt} type="button" aria-pressed={isOn} onClick={() => onValue(isOn ? picked.filter((x) => x !== opt) : [...picked, opt])} className={`${btn} ${isOn ? on : off}`}>{opt}</button>;
        })}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <BrandSelect ariaLabel="Operator" value={op || 'lt'} options={NUM_OPS.map(([k, l]) => ({ value: k, label: l }))} onChange={onOp} minWidth="72px" />
      <input data-chip-control type="number" step="any" min={field.min} max={field.max} aria-label="Value" value={typeof value === 'number' ? value : ''} placeholder={field.min != null ? `${field.min}–${field.max}` : ''}
        onChange={(e) => onValue(e.target.value === '' ? '' : Number(e.target.value))}
        className="h-8 w-24 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2 text-[12px]" />
    </div>
  );
}

export default function EvalConditions({ lineages, value, onChange }) {
  const teamId = getSelectedTeamNumericId();
  const [graphs, setGraphs] = useState({});   // lineage id → graph (for the step list)
  const lines = Array.isArray(value) ? value : [];
  const set = (key, patch) => onChange(lines.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  const remove = (key) => onChange(lines.filter((c) => c.key !== key));
  const evalOptions = [{ value: '', label: 'Any eval' }, ...(lineages || []).map((l) => ({ value: l.id, label: l.name }))];

  const needed = [...new Set(lines.filter((c) => c.what === 'step' && c.lineage_id && !graphs[c.lineage_id]).map((c) => c.lineage_id))];
  useEffect(() => {
    let cancelled = false;
    needed.forEach((id) => {
      Promise.resolve(getLineage(teamId, id)).then((d) => { if (!cancelled) setGraphs((g) => ({ ...g, [id]: d?.current?.graph || { nodes: {} } })); }).catch(() => { if (!cancelled) setGraphs((g) => ({ ...g, [id]: { nodes: {} } })); });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needed.join(','), teamId]);

  return (
    <div className="flex flex-col gap-2" data-testid="eval-conditions">
      {lines.map((c) => {
        const steps = c.what === 'step' && c.lineage_id ? stepsOf(graphs[c.lineage_id]) : [];
        const step = steps.find((s) => s.id === c.node_id) || null;
        const fields = step ? Object.entries(step.output).map(([name, f]) => ({ name, ...f })) : [];
        const field = fields.find((f) => f.name === c.field) || (fields.length === 1 ? fields[0] : null);
        return (
          <div key={c.key} className="flex flex-wrap items-center gap-2 rounded-md border border-[color:var(--border-subtle)] px-2 py-1.5" role="group" aria-label="Eval condition">
            <BrandSelect ariaLabel="Eval" value={c.lineage_id} options={evalOptions} onChange={(v) => set(c.key, { lineage_id: v, node_id: '', field: '', stepValue: null })} minWidth="160px" />
            <BrandSelect ariaLabel="Condition" value={c.what} options={WHAT.filter((w) => w.value !== 'step' || c.lineage_id)} onChange={(v) => set(c.key, { what: v, stepValue: null, value: '' })} minWidth="140px" />
            {c.what === 'reason' ? (
              <input aria-label="Reason codes" value={c.reason} onChange={(e) => set(c.key, { reason: e.target.value })} placeholder="UNGROUNDED, TOO_LONG"
                className="h-8 min-w-[160px] rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2 text-[12px]" />
            ) : null}
            {c.what === 'score' ? (
              <StepValue field={{ name: 'score', type: 'number', min: 0, max: 1 }} value={c.value === '' ? '' : Number(c.value)} op={c.op} onValue={(v) => set(c.key, { value: v })} onOp={(op) => set(c.key, { op })} />
            ) : null}
            {c.what === 'step' ? (
              <>
                <BrandSelect ariaLabel="Step" value={c.node_id} options={[{ value: '', label: steps.length ? 'Choose a step' : (graphs[c.lineage_id] ? 'No steps with an answer' : 'Loading…') }, ...steps.map((s) => ({ value: s.id, label: s.name }))]}
                  onChange={(v) => { const s = steps.find((x) => x.id === v); const fs = s ? Object.keys(s.output) : []; set(c.key, { node_id: v, field: fs.length === 1 ? fs[0] : '', stepValue: null }); }} minWidth="160px" />
                {fields.length > 1 ? (
                  <BrandSelect ariaLabel="Field" value={c.field} options={[{ value: '', label: 'Field' }, ...fields.map((f) => ({ value: f.name, label: f.name }))]} onChange={(v) => set(c.key, { field: v, stepValue: null })} minWidth="120px" />
                ) : null}
                <StepValue field={field} value={c.stepValue} op={c.op} onValue={(v) => set(c.key, { stepValue: v, field: field ? field.name : c.field })} onOp={(op) => set(c.key, { op })} />
              </>
            ) : null}
            <button type="button" onClick={() => remove(c.key)} aria-label="Remove condition" className="ml-auto rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"><X size={13} /></button>
          </div>
        );
      })}
      <div>
        <button type="button" onClick={() => onChange([...lines, newCondition()])} className="inline-flex items-center gap-1 text-[12px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">
          <Plus size={13} /> {lines.length ? 'And' : 'Eval condition'}
        </button>
      </div>
    </div>
  );
}
